// Lib
import React from 'react';

// Hooks
import { useSelector } from 'react-redux';

// Types
import { RootState } from '@app-types/index';

interface ManagedPrivateRouteProps {
  children: React.ReactNode;
  all?: boolean;
  /**
   * Rendered instead of nothing when access is denied. Lets a route show a
   * clean access-gate (e.g. "no access") instead of a blank page or a 404.
   * Defaults to `null` to preserve existing behavior for other routes.
   */
  fallback?: React.ReactNode;
}

/**
 * ManagedPrivateRoute - A wrapper component for protected routes in React Router v6
 *
 * Usage:
 * <Route path="/settings" element={<ManagedPrivateRoute><Settings /></ManagedPrivateRoute>} />
 * <Route path="/onboarding/*" element={<ManagedPrivateRoute all><Onboarding /></ManagedPrivateRoute>} />
 */
const ManagedPrivateRoute: React.FC<ManagedPrivateRouteProps> = ({
  children,
  all = false,
  fallback = null,
}) => {
  const { managedUser } = useSelector((state: RootState) => ({
    managedUser: state?.Managemant?.user,
  }));

  // Render children if:
  // 1. No managed user exists, OR
  // 2. Not in 'all' mode AND managed user has 'edit' permission
  if (
    !managedUser ||
    (!all && !!managedUser && managedUser?.permission === 'edit')
  ) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

export default ManagedPrivateRoute;
